import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  createRouter,
  parseSearchWith,
  RouterProvider,
  stringifySearchWith,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ThemeProvider, TooltipProvider } from "@nexus/ui";
import { queryClient } from "@/lib/query-client";
import { initSentry, Sentry } from "@/lib/sentry";
import { initAnalytics } from "@/lib/analytics";
import { config } from "@/config";
import { isE2ESession } from "@/lib/e2e-session";
import { startInteractionClock } from "@/lib/interaction-clock";
import { NotFoundPage } from "@/components/layout/NotFoundPage";
import { RoutePendingLoader } from "@/components/layout/RoutePendingLoader";
import "@/lib/i18n";
import "@/styles/globals.css";
import { routeTree } from "./routeTree.gen";

window.__NEXUS_SHA__ = config.deploySha;

startInteractionClock();

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultNotFoundComponent: NotFoundPage,
  /**
   * Flat query strings (no JSON `"` around string values).
   * `decode` still coerces digit-only params to numbers — normalize ids in route `validateSearch`.
   */
  parseSearch: parseSearchWith((value: string) => value),
  stringifySearch: stringifySearchWith(JSON.stringify),
  /**
   * Show the destination route's `pendingComponent` immediately when a loader
   * starts running, instead of the router's default 1s grace. Combined with
   * `defaultPendingMinMs: 500` (TanStack default), this gives a smooth
   * post-login skeleton-into-dashboard transition without flicker on cached
   * navigations. Fast loads (<500ms) skip pending entirely.
   */
  defaultPendingMs: 0,
  defaultPendingComponent: RoutePendingLoader,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Playwright E2E sessions are marked via localStorage (see e2e/fixtures.ts)
// before this module evaluates — suppress client telemetry for them so test
// runs don't pollute real analytics/error data.
if (!isE2ESession()) {
  initSentry();
  initAnalytics();
}

function RootErrorFallback({ resetError }: { resetError: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-xl font-normal">{t("errors.pageLoadFailed")}</h1>
      <p className="max-w-md text-sm text-[var(--muted-foreground)]">
        {t("errors.unexpectedErrorDescription")}
      </p>
      <div className="flex gap-3">
        <button
          onClick={resetError}
          className="rounded-md border border-[var(--border)] px-4 py-2 text-sm hover:bg-[var(--accent)] cursor-pointer"
        >
          {t("errors.tryAgain")}
        </button>
        <button
          onClick={() => window.location.reload()}
          className="rounded-md border border-[var(--border)] px-4 py-2 text-sm hover:bg-[var(--accent)] cursor-pointer"
        >
          {t("errors.reloadPage")}
        </button>
      </div>
    </div>
  );
}

const container = document.getElementById("root")!;
// @ts-expect-error – HMR guard: reuse existing root to avoid double-createRoot warning
const root = container._reactRoot ?? (container._reactRoot = createRoot(container));
root.render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={({ resetError }) => <RootErrorFallback resetError={resetError} />}>
      <ThemeProvider>
        <TooltipProvider delayDuration={150}>
          <RouterProvider router={router} />
        </TooltipProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
